import {action, computed, observable} from 'mobx';
import {successAlert} from 'shared/tools';
import {CompanyDocument, NotificationRecipient} from 'main/stores/documents';
import {DomainStore} from 'shared/store';
import {t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import Folder from 'main/stores/folders/Folder';
import {User} from 'main/stores/users';
import _ from 'lodash';

class CompanyDocumentUploadState {
  store = new DomainStore();
  history;
  match;

  @observable acceptedMimeTypes;
  @observable companyDocument;
  @observable isUploading;
  @observable errors = {};
  @observable folders = [];
  @observable users = [];
  
  @action async load(){
    await this.store._compose([
      endpoints.FOLDERS.ALL,
      endpoints.USERS.ACTIVE
    ]);

    this.companyDocument = new CompanyDocument({documentType: this.match.params.documentType});
    this.users = this.store._getAll(types.USER, User)
    this.folders = this.store._getAll(types.FOLDER, Folder);
    
    if (this.companyDocument.documentType === 'pdf_template') {
      this.acceptedMimeTypes = 'application/pdf';
    }
  }

  receiveProps({history, match}){
    this.history = history;
    this.match = match;
  }

  @action async saveCompanyDocument() {
    const {model, errors} = await this.store.post(
      endpoints.COMPANY_DOCUMENTS.all,
      types.COMPANY_DOCUMENT,
      this.companyDocument
    )
    this.errors = errors;

    if (model) {
      successAlert(t('company_settings.documents.alerts.Company document uploaded.'));

      if (model.documentType === 'pdf_template') {
        this.history.push(`/documents/edit/${model.id}`);
      } else {
        this.history.push('/documents');
      }
    }
  }

  @action onUploadStarted() {
    this.isUploading = true;
  }

  @action async onUploadFinished() {
    if (this.companyDocument.documentType === 'pdf_template') {
      await this.validateEncryption();
    }
    this.isUploading = false;
  }

  @action removeFile() {
    this.companyDocument.originalFile = null;
  }

  @action mergeFile(file) {
    this.companyDocument.merge({originalFile: file});
  }

  @action updateFile(files) {
    const file = _.head(files);
    file ? this.mergeFile(file) : this.removeFile();
  }

  @action goBack() {
    this.history.push('/documents');
  }

  @action async validateEncryption() {
    this.errors = {};
    if (!this.companyDocument.originalFile) return;

    const pdfjsLib = await import('pdfjs-dist/build/pdf.mjs');

    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();

    try {
      await pdfjsLib.getDocument({url: this.companyDocument.originalFile.preview}).promise;
    } catch (error) {
      if (error.name === 'PasswordException') {
        this.errors = {
          originalFile: 'Document is password protected. Password protected files are not supported for annotation.'
        };
      } else {
        throw error;
      }
    }
  }

  @computed get companyDocuments() {
    return this.companyDocument.originalFile ? observable([this.companyDocument.originalFile]) : observable([]);
  }

  @computed get canSaveDocument() {
    return !!this.companyDocument.originalFile && !this.errors.originalFile;
  }

  @computed get sortedFolders(){
    return _.orderBy(this.folders, 'order');
  }

  @computed get recipientOptions() {
    return [
      new NotificationRecipient({
        recipientType: 'manager'
      }),
      ...this.users.map(user => new NotificationRecipient(
        {
          recipientType: 'specific_user',
          user: user
        }
      ))
    ]
  }
}

export default CompanyDocumentUploadState;
